import React, {useState, useEffect} from 'react';
import {useAuth} from '../../contexts/AuthContext';
import TopNavigation from '../../components/ui/TopNavigation';
import QRCodeLibraryHeader from './components/QRCodeLibraryHeader';
import {QRCodeGrid} from './components/QRCodeGrid';
import {QRCodeList} from './components/QRCodeList';
import {QRCodeDetailsModal} from './components/QRCodeDetailsModal';
import {QRCodeEditModal} from './components/QRCodeEditModal';
import BulkActions from './components/BulkActions';
import {QRAnalyticsService} from '../../services/qrAnalyticsService';
import {QRSoftDeleteService} from '../../services/qrSoftDeleteService';
import {supabase} from '../../lib/supabase';

export default function QRCodeLibrary() {
    const {user, loading: authLoading} = useAuth();
    const [selectedQRs, setSelectedQRs] = useState([]);
    const [detailsModal, setDetailsModal] = useState({isOpen: false, qrCode: null});
    const [editModal, setEditModal] = useState({isOpen: false, qrCode: null});
    const [isTrashView, setIsTrashView] = useState(false); // New state for trash view

    // Filter states - keep only essential ones
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    // NEW: View state management
    const [currentView, setCurrentView] = useState('grid'); // 'grid' or 'list'
    const [qrCodes, setQrCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('connected');

    // Load QR codes function
    const loadQRCodes = async () => {
        if (!user) {
            setQrCodes([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            let result;

            if (isTrashView) {
                // Load deleted QR codes
                result = await QRSoftDeleteService.getDeletedQRCodes(user.id, 50);
            } else {
                // Load active QR codes
                result = await QRAnalyticsService?.getUserQRCodes(50);
            }

            if (result?.error) {
                setError(`Failed to load QR codes: ${result.error}`);
                setQrCodes([]);
            } else {
                setQrCodes(result.data || []);
            }
        } catch (error) {
            console.error('Error loading QR codes:', error);
            setError(`Failed to load QR codes: ${error?.message}`);
            setQrCodes([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ENHANCED: Real-time subscription for QR codes updates
    useEffect(() => {
        if (!user) {
            setQrCodes([]);
            setIsLoading(false);
            return;
        }

        // Load initial data
        loadQRCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, isTrashView]); // Add isTrashView dependency

    // ENHANCED: Set up real-time subscription for QR codes changes
    useEffect(() => {
        if (!user) return;

        const channel = supabase?.channel('qr_codes_realtime')?.on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'qr_codes',
                filter: `owner_id=eq.${user?.id}`
            },
            (payload) => {
                console.log('Real-time QR codes change:', payload);

                const {eventType, new: newRecord, old: oldRecord} = payload;

                setQrCodes(prev => {
                    switch (eventType) {
                        case 'INSERT':
                            // Add new QR code to the list
                            if (newRecord && !prev?.find(qr => qr?.id === newRecord?.id)) {
                                return [newRecord, ...prev];
                            }
                            return prev;

                        case 'UPDATE':
                            // Update existing QR code in the list
                            if (newRecord) {
                                return prev?.map(qr =>
                                    qr?.id === newRecord?.id ? {
                                        ...qr,
                                        ...newRecord,
                                        // Preserve any local state that might not be in DB
                                        campaigns: newRecord?.campaigns || qr?.campaigns
                                    } : qr
                                );
                            }
                            return prev;

                        case 'DELETE':
                            // Remove deleted QR code from the list
                            if (oldRecord) {
                                return prev?.filter(qr => qr?.id !== oldRecord?.id);
                            }
                            return prev;

                        default:
                            return prev;
                    }
                });

                // ENHANCED: Update modal data if currently viewing the affected QR code
                if (payload?.eventType === 'UPDATE' && payload?.new) {
                    const updatedQR = payload?.new;

                    // Update details modal if showing the same QR
                    if (detailsModal?.qrCode?.id === updatedQR?.id) {
                        setDetailsModal(prev => ({
                            ...prev,
                            qrCode: {...prev?.qrCode, ...updatedQR}
                        }));
                    }

                    // Update edit modal if showing the same QR
                    if (editModal?.qrCode?.id === updatedQR?.id) {
                        setEditModal(prev => ({
                            ...prev,
                            qrCode: {...prev?.qrCode, ...updatedQR}
                        }));
                    }
                }

                // Handle deletion in modals
                if (payload?.eventType === 'DELETE' && payload?.old) {
                    const deletedId = payload?.old?.id;

                    if (detailsModal?.qrCode?.id === deletedId) {
                        setDetailsModal({isOpen: false, qrCode: null});
                    }

                    if (editModal?.qrCode?.id === deletedId) {
                        setEditModal({isOpen: false, qrCode: null});
                    }

                    // Remove from selected QRs
                    setSelectedQRs(prev => prev?.filter(id => id !== deletedId));
                }
            }
        )?.subscribe((status) => {
            console.log('QR codes real-time subscription status:', status);
            // FIXED: Update connection status based on subscription status
            if (status === 'SUBSCRIBED') {
                setConnectionStatus('connected');
            } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
                setConnectionStatus('disconnected');
            } else {
                setConnectionStatus('connecting');
            }
        });

        // ENHANCED: Subscribe to QR scans for real-time scan count updates
        const scansChannel = supabase?.channel('qr_scans_realtime')
            ?.on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'qr_scans'
                },
                (payload) => {
                    console.log('Real-time QR scan:', payload);

                    if (payload?.new?.qr_code_id) {
                        // Update scan count for the affected QR code
                        setQrCodes(prev => prev?.map(qr => {
                            if (qr?.id === payload?.new?.qr_code_id) {
                                const currentScans = qr?.total_scans || 0;
                                return {
                                    ...qr,
                                    total_scans: currentScans + 1,
                                    last_scanned: payload?.new?.created_at
                                };
                            }
                            return qr;
                        }));

                        // Update modal data if viewing the scanned QR
                        const scannedId = payload?.new?.qr_code_id;
                        if (detailsModal?.qrCode?.id === scannedId) {
                            setDetailsModal(prev => {
                                if (!prev?.qrCode) return prev;
                                const currentScans = prev?.qrCode?.total_scans || 0;
                                return {
                                    ...prev,
                                    qrCode: {
                                        ...prev?.qrCode,
                                        total_scans: currentScans + 1,
                                        last_scanned: payload?.new?.created_at
                                    }
                                };
                            });
                        }
                    }
                }
            )
            ?.subscribe((status) => {
                console.log('QR scans real-time subscription status:', status);
            });

        // Cleanup subscriptions
        return () => {
            console.log('Unsubscribing from real-time channels');
            supabase?.removeChannel(channel);
            supabase?.removeChannel(scansChannel);
        };
    }, [user]); // Only depend on user, not other state

    // Filter QR codes for both views
    const filteredQRCodes = qrCodes?.filter(qr => {
        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm?.toLowerCase();
            const matchesName = (qr?.name || '')?.toLowerCase()?.includes(searchLower);
            const matchesContent = (qr?.content || '')?.toLowerCase()?.includes(searchLower);
            if (!matchesName && !matchesContent) {
                return false;
            }
        }

        // Type filter only
        if (typeFilter !== 'all' && qr?.qr_type !== typeFilter) {
            return false;
        }

        return true;
    });

    // Handle QR selection
    const handleSelectQR = (qrId, isSelected) => {
        setSelectedQRs(prev => {
            if (isSelected) {
                return prev?.includes(qrId) ? prev : [...prev, qrId];
            } else {
                return prev?.filter(id => id !== qrId);
            }
        });
    };

    // Handle bulk actions
    const handleBulkAction = async (action) => {
        if (!user || selectedQRs.length === 0) return;

        console.log(`Performing ${action} on QRs:`, selectedQRs);

        try {
            let result;

            switch (action) {
                case 'delete':
                    // Soft delete (move to trash)
                    result = await QRSoftDeleteService.bulkSoftDeleteQRCodes(selectedQRs, user.id);
                    break;
                case 'restore':
                    // Restore from trash
                    result = await QRSoftDeleteService.bulkRestoreQRCodes(selectedQRs, user.id);
                    break;
                case 'permanent_delete':
                    // Permanently delete
                    result = await QRSoftDeleteService.bulkPermanentlyDeleteQRCodes(selectedQRs, user.id);
                    break;
                case 'activate':
                    // Activate QR codes
                    result = await QRAnalyticsService.bulkUpdateQRCodes(selectedQRs, {is_active: true});
                    break;
                case 'deactivate':
                    // Deactivate QR codes
                    result = await QRAnalyticsService.bulkUpdateQRCodes(selectedQRs, {is_active: false});
                    break;
                default:
                    console.warn(`Unknown bulk action: ${action}`);
                    return;
            }

            if (result?.success) {
                console.log(`Bulk ${action} completed:`, result);

                // Show feedback about results
                if (result.successful && result.failed) {
                    alert(`Success: ${result.successful} QR codes ${action}d\nFailed: ${result.failed} QR codes (may be already deleted or not owned by you)`);
                } else if (result.successful) {
                    console.log(`✅ Successfully ${action}d ${result.successful} QR code(s)`);
                }

                // Refresh the QR codes list
                loadQRCodes();
                // Clear selection
                setSelectedQRs([]);
            } else {
                console.error(`Bulk ${action} failed:`, result?.error);
                alert(`Failed to ${action} QR codes: ${result?.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error(`Error performing bulk ${action}:`, error);
        }
    };

    // Handle create new QR
    const handleCreateNew = () => {
        // Navigate to QR generator or open modal
        window.location.href = '/qr-generator';
    };

    // Show details modal
    const showQRDetails = (qrCode) => {
        setDetailsModal({isOpen: true, qrCode});
    };

    // Show edit modal
    const showQREdit = (qrCode) => {
        setEditModal({isOpen: true, qrCode});
        setDetailsModal({isOpen: false, qrCode: null}); // Close details modal
    };

    // ENHANCED: Handle QR save after edit with optimistic updates
    const handleQRSave = (updatedQR) => {
        if (!updatedQR?.id) return;

        // REMOVED: Manual state updates since real-time subscriptions handle this
        // Real-time subscription will automatically update the state

        // Close edit modal
        setEditModal({isOpen: false, qrCode: null});

        // Optional: Show success message or feedback
        console.log('QR code updated successfully:', updatedQR);
    };

    // ENHANCED: Handle QR deletion with refresh
    const handleQRDelete = (qrCode) => {
        console.log('QR code deleted, refreshing list...');

        // Close modals immediately for better UX
        setDetailsModal({isOpen: false, qrCode: null});
        setEditModal({isOpen: false, qrCode: null});

        // Refresh the list after soft delete (real-time won't detect soft deletes)
        setTimeout(() => {
            loadQRCodes();
        }, 500); // Small delay to ensure database is updated
    };

    // Handle permanent delete from trash
    const handlePermanentDelete = async (qrCode) => {
        if (!user || !qrCode?.id) return;

        console.log('🗑️ Permanently deleting QR code:', qrCode?.id);

        try {
            const result = await QRSoftDeleteService.bulkPermanentlyDeleteQRCodes([qrCode?.id], user.id);

            console.log(result);
            if (result?.success && result?.successful > 0) {
                console.log('✅ QR code permanently deleted');
                // Close modals
                setDetailsModal({isOpen: false, qrCode: null});
                setEditModal({isOpen: false, qrCode: null});
                // Refresh list
                setTimeout(() => {
                    loadQRCodes();
                }, 500);
            } else {
                alert(`Failed to permanently delete: ${result?.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('❌ Permanent delete error:', error);
            alert(`Failed to permanently delete: ${error?.message}`);
        }
    };

    // ENHANCED: Handle QR update from components (now redundant due to real-time)
    const handleQRUpdate = (updatedQR) => {
        // This is now handled automatically by real-time subscriptions
        // Keeping for backwards compatibility with existing components
        console.log('QR update event (handled by real-time):', updatedQR);
    };

    // REMOVED: Problematic real-time connection status monitoring
    // The original useEffect with supabase?.realtime?.onConnStateChange has been removed
    // as this method doesn't exist in the current Supabase version

    if (authLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading library...</p>
                </div>
            </div>
        );
    }

    // Not authenticated state
    if (!user) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">QR Code Library</h1>
                    <p className="text-gray-600 mb-4">Please sign in to view your QR codes</p>
                    <button
                        onClick={() => window.location.href = '/auth'}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                    >
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <TopNavigation/>

            {/* ENHANCED: Real-time connection status indicator with proper status values */}
            {connectionStatus !== 'connected' && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-2">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                {connectionStatus === 'connecting' ? 'Connecting to real-time updates...' :
                                    connectionStatus === 'disconnected' ? 'Real-time updates disconnected. Attempting to reconnect...' : 'Real-time connection status: ' + connectionStatus}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
                <div className="max-w-7xl mx-auto">
                    {/* Enhanced Page Header with View Toggle */}
                    <QRCodeLibraryHeader
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        selectedQRs={selectedQRs}
                        onBulkAction={handleBulkAction}
                        typeFilter={typeFilter}
                        onTypeFilterChange={setTypeFilter}
                        selectedCount={selectedQRs?.length}
                        statusFilter="all"
                        onStatusFilterChange={() => {
                        }}
                        campaignFilter="all"
                        onCampaignFilterChange={() => {
                        }}
                        dateRange="all"
                        onDateRangeChange={() => {
                        }}
                        onCreateNew={handleCreateNew}
                        // NEW: Trash view props
                        isTrashView={isTrashView}
                        onTrashToggle={setIsTrashView}
                        // NEW: View toggle props
                        currentView={currentView}
                        onViewChange={setCurrentView}
                    />

                    {/* Bulk Actions */}
                    {selectedQRs.length > 0 && (
                        <BulkActions
                            selectedCount={selectedQRs.length}
                            totalCount={filteredQRCodes.length}
                            onSelectAll={() => {
                                if (selectedQRs.length === filteredQRCodes.length) {
                                    setSelectedQRs([]);
                                } else {
                                    setSelectedQRs(filteredQRCodes.map(qr => qr.id));
                                }
                            }}
                            onBulkAction={handleBulkAction}
                            onCancel={() => setSelectedQRs([])}
                            isTrashView={isTrashView}
                        />
                    )}

                    {/* Dynamic View Rendering */}
                    {currentView === 'grid' ? (
                        <QRCodeGrid
                            searchTerm={searchTerm}
                            typeFilter={typeFilter}
                            onSelectQR={handleSelectQR}
                            selectedQRs={selectedQRs}
                            onBulkAction={handleBulkAction}
                            onShowDetails={showQRDetails}
                            refreshTrigger={0} // Not needed with real-time updates
                            onDelete={handleQRDelete}
                            onPermanentDelete={handlePermanentDelete}
                            onUpdate={handleQRUpdate}
                            // ENHANCED: Pass real-time data
                            qrCodes={filteredQRCodes}
                            isLoading={isLoading}
                            error={error}
                            isTrashView={isTrashView}
                        />
                    ) : (
                        <QRCodeList
                            qrCodes={filteredQRCodes}
                            isLoading={isLoading}
                            error={error}
                            onSelectQR={handleSelectQR}
                            selectedQRs={selectedQRs}
                            onShowDetails={showQRDetails}
                            onDelete={handleQRDelete}
                            onPermanentDelete={handlePermanentDelete}
                            onUpdate={handleQRUpdate}
                            isTrashView={isTrashView}
                        />
                    )}

                    {/* Details Modal */}
                    <QRCodeDetailsModal
                        qrCode={detailsModal?.qrCode}
                        isOpen={detailsModal?.isOpen}
                        onClose={() => setDetailsModal({isOpen: false, qrCode: null})}
                        onEdit={(qrCode) => showQREdit(qrCode || detailsModal?.qrCode)}
                        onDelete={handleQRDelete}
                    />

                    {/* Edit Modal */}
                    <QRCodeEditModal
                        qrCode={editModal?.qrCode}
                        isOpen={editModal?.isOpen}
                        onClose={() => setEditModal({isOpen: false, qrCode: null})}
                        onSave={handleQRSave}
                    />
                </div>
            </main>
        </div>
    );
}